import { IContextualMenuItem } from '@fluentui/react';
import { isNil } from 'lodash';
import React, { useCallback } from 'react';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  customizeFormatAction,
  resetAliasReplacementAction,
  updateAliasReplacementAction,
} from '../../../../redux/slices/analyses';
import {
  IMeasureDisplayFormatFieldsFragment,
  Order,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { IAliasOption } from '../../../AliasEditor';
import AliasEditorWrapper from '../../../AliasEditor/AliasEditorWrapper';
import ContextualMenuSubItemWrapper from '../../../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import FormatMenu from '../../ChartLegend/ChartMeasures/FormatMenu';
import { IDisplayedMeasure } from '../../interfaces';
import * as ChartDataTableUI from '../ChartDataTableUI';
import { useChartDataTable } from '../useChartDataTable';
import ContextualMenuOption from '../../../Dropdown/ContextualMenu/ContextualMenuOption';
import { TableSortIcon } from '../../../Icon';
import { useChartContext } from '../../ChartContext';
import { assertCondition } from '../../../../utils/types';
import KubitTooltip from '../../../KubitTooltip';

interface IProps {
  analysisId: string;
  measureIndex: number;
  rowIndex: number;
  columnIndex: number;
  style: React.CSSProperties;
  dataTableData: ReturnType<typeof useChartDataTable>;
  headerName: string;
  onSort: (params: { index: number }) => void;
  handleMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    columnIndex: number,
  ) => void;
  isColumnSorted?: boolean;
  sortDirection: Order | undefined;
  isResizing: boolean;
}

const useHandleChangeFormat = (analysisId: string) => {
  const dispatch = useAppDispatch();
  return useCallback(
    (
      measure: IDisplayedMeasure,
      dismissMenu: (ev?: unknown, dismissAll?: boolean) => void,
    ) =>
      (values: IMeasureDisplayFormatFieldsFragment) => {
        dispatch(
          customizeFormatAction({
            analysisId,
            measureKey: measure.key,
            customMeasureFormat: values,
          }),
        );

        dismissMenu(undefined, true);
      },
    [analysisId, dispatch],
  );
};

const HeaderCell = ({
  analysisId,
  measureIndex,
  rowIndex,
  columnIndex,
  style,
  dataTableData,
  headerName,
  onSort,
  handleMouseDown,
  isColumnSorted,
  sortDirection,
  isResizing,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { chartData, displayedMeasures } = useChartContext();

  const { hasComparison, segments } = dataTableData;

  assertCondition(chartData.queryType === QueryType.DataTable);
  const measureControl = chartData.measureControls[measureIndex];
  const userAliasId = measureControl.userAlias.aliasId;
  const aliases: IAliasOption[] = [
    {
      type: 'alias',
      analysisId,
      name: measureControl.name,
      userAliasId,
      apply: (newAlias) => {
        dispatch(
          updateAliasReplacementAction({
            analysisId,
            aliasId: userAliasId,
            replacement: newAlias,
          }),
        );
      },
      reset: () => {
        dispatch(
          resetAliasReplacementAction({
            analysisId,
            aliasId: userAliasId,
          }),
        );
      },
    },
  ];

  const handleChangeFormat = useHandleChangeFormat(analysisId);

  const getExtraMenuItems = useCallback(
    (measure: IDisplayedMeasure) => {
      const menuItems: IContextualMenuItem[] = [
        {
          key: 'Format',
          onRenderContent: () => (
            <ContextualMenuOption label={'Format'} hasSubMenus={true} />
          ),
          subMenuProps: {
            items: [
              {
                key: 'format-menu',
                onRender: (_: unknown, dismissMenu) => (
                  <ContextualMenuSubItemWrapper>
                    <FormatMenu
                      measureKey={measure.key}
                      onSubmit={handleChangeFormat(measure, dismissMenu)}
                      hideCalculation={false}
                    />
                  </ContextualMenuSubItemWrapper>
                ),
              },
            ],
          },
        },
      ];

      return menuItems;
    },
    [handleChangeFormat],
  );

  let measureChartOptions: IContextualMenuItem[] = [];

  // if displayedMeasures is 0, it means this chart did not migrate to the new chartPref
  // for data table yet. don't show the format option in this case.
  if (displayedMeasures.length > 0) {
    measureChartOptions = getExtraMenuItems(displayedMeasures[measureIndex]);
  }

  const handleSort = (index: number) => {
    if (hasComparison || segments.length > 0) {
      return;
    }
    onSort({ index });
  };

  const isSorted = isColumnSorted && !isNil(sortDirection);

  return (
    <ChartDataTableUI.HeaderCell
      key={`Data-Table-Header-Cell-Row-${rowIndex}-Col-${columnIndex}`}
      style={style}
      $columnIndex={columnIndex}
      $rowIndex={rowIndex}
      $isBackgroundFilled={true}
      title={headerName}
    >
      <AliasEditorWrapper
        aliases={aliases}
        inline={false}
        extraMenuItems={measureChartOptions}
        disabled={isResizing}
      >
        <ChartDataTableUI.SortableContainer
          onClick={() => handleSort(columnIndex)}
        >
          <KubitTooltip
            content={sortDirection === Order.Asc ? 'Ascending' : 'Descending'}
            disabled={!isSorted}
            placement="bottom"
          >
            <ChartDataTableUI.SortableTitle $isColumnSorted={isColumnSorted}>
              {headerName}
            </ChartDataTableUI.SortableTitle>
          </KubitTooltip>

          {isSorted && (
            <TableSortIcon
              isSortAsc={sortDirection === Order.Asc}
              isActive={true}
            />
          )}
        </ChartDataTableUI.SortableContainer>
      </AliasEditorWrapper>
      {segments.length === 0 && !hasComparison && (
        <ChartDataTableUI.ChartColumnResizeHandle
          onMouseDown={(e) => handleMouseDown(e, columnIndex)}
        />
      )}
    </ChartDataTableUI.HeaderCell>
  );
};

export default HeaderCell;
